import {
  Button,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FilledButton } from "../../customComponents/styled/styledButtons";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import { useGetClassesQuery } from "../classroom/classApiSlice";
import {
  useGetCurrentTermQuery,
} from "../settings/session/sessionApiSlice";
import toast from "../../../helpers/toast";
import {
  useCreateRecordMutation,
  useDeleteRecordsBySubjectClassAndAssessmentMutation,
  useGetRecordsBySubjectAndClassQuery,
} from "../quick-entry/recordApiSlice";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";
import AIScoreExtractModal from "../../customComponents/AIScoreExtractModal";

function computeScore(subScores, obtainable) {
  const totalObtained = subScores.reduce((sum, s) => sum + (parseFloat(s.obtained) || 0), 0);
  const totalObtainable = subScores.reduce((sum, s) => sum + (parseFloat(s.obtainable) || 0), 0);
  if (totalObtainable <= 0) return 0;
  return Math.round(Math.min((totalObtained / totalObtainable) * obtainable, obtainable) * 10) / 10;
}

const toNumberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sanitizeSubScoresForSave = (subScores = []) =>
  subScores.map((s) => {
    const obtainable = Math.max(0, toNumberOrZero(s?.obtainable));
    const obtained = Math.max(0, Math.min(toNumberOrZero(s?.obtained), obtainable));
    return { obtainable, obtained };
  });

const hasAnyUserInput = (subScores = []) =>
  subScores.some((s) => s?.obtainable !== "" || s?.obtained !== "");

const normalizeSubScores = (subScores = []) =>
  subScores.map((s) => ({
    obtainable: Number(s?.obtainable) || 0,
    obtained: Number(s?.obtained) || 0,
  }));

const serializeSubScores = (subScores = []) =>
  JSON.stringify(normalizeSubScores(subScores));

function ScoreSheet({
  myAssessments,
  activeAssessment,
  mySubjects,
  activeSubject,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [scoresheet, setScoresheet] = useState([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [openAiExtract, setOpenAiExtract] = useState(false);
  const handleSaveRef = useRef(null);

  const activeSubjectId = mySubjects[activeSubject]?.id;
  const activeClassroomId = mySubjects[activeSubject]?.classroom_id;
  const skipSubjectQueries = !activeSubjectId || !activeClassroomId;

  const { data: students, isLoadingStudents } = useGetClassesQuery(
    `${activeClassroomId}/students`,
    { skip: !activeClassroomId }
  );

  const { data: term } = useGetCurrentTermQuery();

  const { data: records, isLoading: isLoadingRecords } =
    useGetRecordsBySubjectAndClassQuery(
      { subject_id: activeSubjectId, classroom_id: activeClassroomId, include_raw: true },
      { skip: skipSubjectQueries }
    );

  const originalAssessmentRecordMap = useMemo(() => {
    const map = new Map();
    const currentAssessmentId = myAssessments[activeAssessment]?.id;
    if (!currentAssessmentId) {
      return map;
    }

    const obtainable = myAssessments[activeAssessment]?.obtainable_score ?? 0;
    (records || [])
      .filter(
        (item) =>
          Number(item.assessment?.id ?? item.assessment_id) ===
          Number(currentAssessmentId)
      )
      .forEach((item) => {
        const fallbackSubScores = [{ obtainable, obtained: Number(item.score) || 0 }];
        const rawScores = item.raw?.scores?.length ? item.raw.scores : fallbackSubScores;
        map.set(String(item.student_id), {
          score: Number(item.score) || 0,
          raw: serializeSubScores(rawScores),
        });
      });

    return map;
  }, [records, myAssessments, activeAssessment]);

  const [createRecord, { isLoading: createRecordLoading }] =
    useCreateRecordMutation();
  const [deleteRecord, { isLoading: deleteRecordLoading }] =
    useDeleteRecordsBySubjectClassAndAssessmentMutation();

  const ensureSubScoreAtIndex = (subScores = [], index) => {
    const next = [...subScores];
    while (next.length <= index) {
      next.push({ obtainable: "", obtained: "" });
    }
    return next;
  };

  useEffect(() => {
    const assessmentId = myAssessments[activeAssessment]?.id;
    const obtainable = myAssessments[activeAssessment]?.obtainable_score;
    const assessmentRecords = records?.filter(
      (item) => item.assessment.id === assessmentId
    );

    setScoresheet(
      assessmentRecords?.map((item) => {
        const subScores =
          item.raw?.scores?.length > 0
            ? item.raw.scores
            : [{ obtainable: obtainable ?? 0, obtained: item.score ?? 0 }];
        return {
          student_id: item.student_id.toString(),
          assessment_id: item.assessment_id,
          subScores,
        };
      }) ?? []
    );
    setActiveSheetIndex(0);
  }, [records, myAssessments, activeAssessment]);

  const sheetCount = useMemo(() => {
    const maxSheets = scoresheet.reduce(
      (maxCount, entry) => Math.max(maxCount, entry?.subScores?.length || 0),
      0
    );
    return Math.max(1, maxSheets);
  }, [scoresheet]);

  useEffect(() => {
    if (activeSheetIndex > sheetCount - 1) {
      setActiveSheetIndex(Math.max(0, sheetCount - 1));
    }
  }, [activeSheetIndex, sheetCount]);

  const getEntry = (studentId) =>
    scoresheet.find((item) => item.student_id === studentId.toString());

  const getSubScores = (studentId) =>
    getEntry(studentId)?.subScores ?? [{ obtainable: "", obtained: "" }];

  const handleAddSheet = () => {
    const nextIndex = sheetCount;
    setScoresheet((prev) => {
      const byStudentId = new Map(prev.map((entry) => [entry.student_id, entry]));
      students?.forEach((student) => {
        const studentId = student.id.toString();
        const existing = byStudentId.get(studentId);
        const currentSubScores = ensureSubScoreAtIndex(existing?.subScores || [], nextIndex);
        byStudentId.set(studentId, {
          student_id: studentId,
          assessment_id: myAssessments[activeAssessment]?.id,
          subScores: currentSubScores,
        });
      });
      return Array.from(byStudentId.values());
    });
    setActiveSheetIndex(nextIndex);
  };

  const handleDeleteActiveSheet = () => {
    if (sheetCount <= 1) {
      toast.info("At least one sheet is required");
      return;
    }

    setScoresheet((prev) =>
      prev.map((entry) => {
        const remaining = (entry.subScores || []).filter((_, i) => i !== activeSheetIndex);
        return {
          ...entry,
          subScores: remaining.length > 0 ? remaining : [{ obtainable: "", obtained: "" }],
        };
      })
    );
    setActiveSheetIndex((prev) => Math.max(0, Math.min(prev, sheetCount - 2)));
    toast.success(`Deleted Sheet ${activeSheetIndex + 1}`);
  };

  const handleStudentObtainableChange = (value) => {
    if (!students?.length) {
      return;
    }

    const normalizedValue = value === "" ? "" : Math.max(0, toNumberOrZero(value));

    setScoresheet((prev) => {
      const byStudentId = new Map(prev.map((entry) => [entry.student_id, entry]));

      students.forEach((student) => {
        const studentId = student.id.toString();
        const existingEntry = byStudentId.get(studentId);
        const currentSubScores = ensureSubScoreAtIndex(existingEntry?.subScores || [], activeSheetIndex);

        const currentObtained = currentSubScores[activeSheetIndex]?.obtained;
        const clampedObtained =
          currentObtained === ""
            ? ""
            : Math.min(Math.max(0, toNumberOrZero(currentObtained)), normalizedValue === "" ? Number.MAX_SAFE_INTEGER : normalizedValue);

        currentSubScores[activeSheetIndex] = {
          ...currentSubScores[activeSheetIndex],
          obtainable: normalizedValue,
          obtained: clampedObtained,
        };

        byStudentId.set(studentId, {
          student_id: studentId,
          assessment_id: myAssessments[activeAssessment]?.id,
          subScores: currentSubScores,
        });
      });

      return Array.from(byStudentId.values());
    });
  };

  const handleStudentObtainedChange = (studentId, value) => {
    const normalizedValue = value === "" ? "" : Math.max(0, toNumberOrZero(value));
    setScoresheet((prev) => {
      const byStudentId = new Map(prev.map((entry) => [entry.student_id, entry]));
      const key = String(studentId);
      const existingEntry = byStudentId.get(key);
      const currentSubScores = ensureSubScoreAtIndex(existingEntry?.subScores || [], activeSheetIndex);
      const obtainable = currentSubScores[activeSheetIndex]?.obtainable;
      const maxAllowed = obtainable === "" ? Number.MAX_SAFE_INTEGER : Math.max(0, toNumberOrZero(obtainable));

      currentSubScores[activeSheetIndex] = {
        ...currentSubScores[activeSheetIndex],
        obtained: normalizedValue === "" ? "" : Math.min(normalizedValue, maxAllowed),
      };

      byStudentId.set(key, {
        student_id: key,
        assessment_id: myAssessments[activeAssessment]?.id,
        subScores: currentSubScores,
      });

      return Array.from(byStudentId.values());
    });
  };

  const handleSave = async () => {
    try {
      const obtainable = myAssessments[activeAssessment]?.obtainable_score;
      const preparedRecords = scoresheet
        .filter((entry) => hasAnyUserInput(entry.subScores))
        .map((entry) => {
          const safeSubScores = sanitizeSubScoresForSave(entry.subScores);
          return {
            student_id: entry.student_id,
            assessment_id: entry.assessment_id,
            score: computeScore(safeSubScores, obtainable),
            raw: { scores: safeSubScores },
          };
        });

      const changedRecords = preparedRecords.filter((entry) => {
        const existing = originalAssessmentRecordMap.get(String(entry.student_id));
        if (!existing) {
          return true;
        }

        const nextScore = Number(entry.score) || 0;
        const nextRaw = serializeSubScores(entry.raw?.scores || []);
        return existing.score !== nextScore || existing.raw !== nextRaw;
      });

      if (changedRecords.length === 0) {
        toast.info("No score changes to save");
        return;
      }

      const body = {
        subject_id: mySubjects[activeSubject]?.id,
        classroom_id: mySubjects[activeSubject]?.classroom_id,
        records: changedRecords,
      };
      const response = await createRecord(body).unwrap();
      if (response) {
        toast.success("Score sheet saved successfully");
      }
    } catch (error) {
      toast.error(error?.message || "An error occured while saving the score sheet");
    }
  };

  const handleDelete = async () => {
    try {
      const values = {
        subjectId: mySubjects[activeSubject]?.id,
        classId: mySubjects[activeSubject]?.classroom_id,
        assessmentId: myAssessments[activeAssessment]?.id,
      };
      const response = await deleteRecord(values).unwrap();
      if (response) {
        toast.success("record deleted successfully");
        setScoresheet([]);
      }
    } catch (error) {
      toast.error("Error deleting score sheet");
    }
  };

  useEffect(() => {
    handleSaveRef.current = handleSave;
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSaveRef.current?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const assessmentObtainable = myAssessments[activeAssessment]?.obtainable_score;

  return (
    <Grid container spacing={3}>
      <CustomWarningDialog
        open={openWarningDialog}
        setOpen={setOpenWarningDialog}
        action={handleDelete}
        message="!!!WARNING!!! Irreversable Action!! are sure you want to clear these record?"
      />
      <Grid item xs={12} md={11}>
        <StyledPaper>
          <Box className="paperHeader">
            <Typography
              variant="formHeading"
              sx={{
                textAlign: "center",
                color: "primary.main",
                textTransform: "capitalize",
              }}
            >
              {myAssessments[activeAssessment]?.name} score sheet
            </Typography>
          </Box>
          <Box className="paperBody" sx={{ pb: isMobile ? 10 : 0 }}>
            {isLoadingStudents && <LinearProgress />}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 1, flexWrap: "wrap" }}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                {Array.from({ length: sheetCount }).map((_, index) => (
                  <Button
                    key={index}
                    variant={activeSheetIndex === index ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setActiveSheetIndex(index)}
                  >
                    Sheet {index + 1}
                  </Button>
                ))}
              </Stack>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Add a new sheet">
                  <IconButton size="small" color="primary" onClick={handleAddSheet}>
                    <AddCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete active sheet">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={handleDeleteActiveSheet}
                      disabled={sheetCount <= 1}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Each sheet represents one sub-score layer. Editing any obtainable on this sheet applies to all students on this sheet.
            </Typography>
            <Grid container spacing={2}>
              {students?.map((student) => {
                const subScores = getSubScores(student.id);
                const computed = computeScore(subScores, assessmentObtainable);
                const activeSheet = ensureSubScoreAtIndex(subScores, activeSheetIndex)[activeSheetIndex];
                return (
                  <React.Fragment key={student.id}>
                    <Grid item xs={12} lg={8}>
                      <FormControl fullWidth>
                        <StyledTextField
                          id="name"
                          name="name"
                          value={student.first_name + " " + student.last_name}
                          bold="true"
                          disabled
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <FormControl fullWidth>
                        <StyledTextField
                          id="student_id"
                          name="student_id"
                          value={student.reg_number}
                          bold="true"
                          disabled
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <FormControl fullWidth>
                        <Typography variant="formSubHeading">Obtainable</Typography>
                        <StyledTextField
                          type="number"
                          inputProps={{ step: "any", min: 0, tabIndex: -1 }}
                          value={activeSheet?.obtainable ?? ""}
                          onChange={(e) => handleStudentObtainableChange(e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <FormControl fullWidth>
                        <Typography variant="formSubHeading">Obtained</Typography>
                        <StyledTextField
                          type="number"
                          inputProps={{ step: "any", min: 0 }}
                          value={activeSheet?.obtained ?? ""}
                          onChange={(e) => handleStudentObtainedChange(student.id, e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <Typography variant="formSubHeading">
                          Computed Score / {assessmentObtainable}
                        </Typography>
                        <StyledTextField
                          type="number"
                          value={computed.toFixed(1)}
                          bold="true"
                          disabled
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>
                  </React.Fragment>
                );
              })}
            </Grid>
          </Box>
        </StyledPaper>
      </Grid>
      {!isMobile && (
      <Grid item xs={12} md={1}>
        <Stack
          direction={{ xs: "row", md: "column" }}
          spacing={1}
          sx={{
            "& .btn-icon": {
              fontSize: "14px",
            },
          }}
        >
          {createRecordLoading ? (
            <CircularProgress size={28} />
          ) : (
            <Tooltip placement="left" title="save">
              <FilledButton>
                <SaveIcon className="btn-icon" onClick={handleSave} />
              </FilledButton>
            </Tooltip>
          )}
          {deleteRecordLoading ? (
            <CircularProgress size={28} />
          ) : (
            <Tooltip placement="left" title="Clear records">
              <FilledButton onClick={() => setOpenWarningDialog(true)}>
                <DeleteIcon className="btn-icon" />
              </FilledButton>
            </Tooltip>
          )}
          <Tooltip placement="left" title="Extract scores from image/doc/audio">
            <FilledButton onClick={() => setOpenAiExtract(true)}>
              <AutoAwesomeIcon className="btn-icon" />
            </FilledButton>
          </Tooltip>
        </Stack>
      </Grid>
      )}

      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (zIndex) => zIndex.appBar,
            backgroundColor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            px: 2,
            py: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{
              "& .btn-icon": {
                fontSize: "14px",
              },
            }}
          >
            {createRecordLoading ? (
              <CircularProgress size={28} />
            ) : (
              <Tooltip title="save">
                <FilledButton>
                  <SaveIcon className="btn-icon" onClick={handleSave} />
                </FilledButton>
              </Tooltip>
            )}
            {deleteRecordLoading ? (
              <CircularProgress size={28} />
            ) : (
              <Tooltip title="Clear records">
                <FilledButton onClick={() => setOpenWarningDialog(true)}>
                  <DeleteIcon className="btn-icon" />
                </FilledButton>
              </Tooltip>
            )}
            <Tooltip title="Extract scores from image/doc/audio">
              <FilledButton onClick={() => setOpenAiExtract(true)}>
                <AutoAwesomeIcon className="btn-icon" />
              </FilledButton>
            </Tooltip>
          </Stack>
        </Box>
      )}

      {/* AI Score Extraction Modal */}
      <AIScoreExtractModal
        open={openAiExtract}
        onClose={() => setOpenAiExtract(false)}
        subjectId={activeSubjectId}
        classroomId={activeClassroomId}
        termId={term?.id || null}
        assessmentId={myAssessments[activeAssessment]?.id || null}
        assessments={myAssessments || []}
        students={students || []}
        onApply={(extracted) => {
          // Merge AI-extracted scores into scorehseet state
          setScoresheet((prev) => {
            const updated = [...prev];
            for (const item of extracted) {
              const idx = updated.findIndex(
                (r) =>
                  r.student_id === String(item.student_id) &&
                  r.assessment_id === item.assessment_id
              );
              const obtainable = myAssessments[activeAssessment]?.obtainable_score ?? 0;
              const entry = {
                student_id: String(item.student_id),
                assessment_id: item.assessment_id,
                subScores: [{ obtained: item.score, obtainable }],
              };
              if (idx >= 0) {
                updated[idx] = entry;
              } else {
                updated.push(entry);
              }
            }
            return updated;
          });
        }}
      />
    </Grid>
  );
}

export default ScoreSheet;
